import { CommonModule } from '@angular/common';
import { Component, ComponentFactoryResolver, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewContainerRef } from '@angular/core';
import { GridComponentTypes, Job, Page, PageComponentTypes, TemplateRow, VoyseComponent } from '@interfaces';
import { AbstractComponent } from '../abstract-component';

@Component({
    selector: 'voyse-component-resolver',
    templateUrl: './component-resolver.component.html',
    standalone: true,
    imports: [CommonModule],
})
export class ComponentResolverComponent implements OnInit, OnChanges, OnDestroy {
    @Input() componentIndex: number;
    @Input() rowIndex: number;
    @Input() row: TemplateRow;
    @Input() gridIndex: number;
    @Input() component: VoyseComponent;
    @Input() noTracking = false;
    @Input() type = null;
    @Input() editMode = false;
    @Input() templateLocked = false;
    @Input() mobileMode = true;
    @Input() moveable = true;
    @Input() entity: Job | Page = null;
    public published = false;

    // Cache for component factories to avoid repeated resolution
    private static componentFactoryCache = new Map<string, any>();

    // Track current component type to avoid unnecessary recreation
    private currentComponentType: string = null;
    private currentComponentRef: any = null;

    private gridComponentMap = {
        [GridComponentTypes.TEXT]: {
            fetch: () => import('../../grid-components/textblock/textblock.component'),
            componentName: 'TextblockComponent'
        },
        [GridComponentTypes.IMAGE]: {
            fetch: () => import('../../grid-components/imageblock/imageblock.component'),
            componentName: 'ImageblockComponent'
        },
        [GridComponentTypes.QUOTE]: {
            fetch: () => import('../../grid-components/quoteblock/quoteblock.component'),
            componentName: 'QuoteblockComponent'
        },
        [GridComponentTypes.VIDEO]: {
            fetch: () => import('../../grid-components/videoblock/videoblock.component'),
            componentName: 'VideoblockComponent'
        },
        [GridComponentTypes.MAP]: {
            fetch: () => import('../../grid-components/husk/husk.component'),
            componentName: 'HuskComponent'
        },
        [GridComponentTypes.JOB_HEADER]: {
            fetch: () => import('../../grid-components/job-header-block/job-header-block.component'),
            componentName: 'JobHeaderBlockComponent'
        },
        [GridComponentTypes.JOB_DETAILS]: {
            fetch: () => import('../../grid-components/job-details-block/job-details-block.component'),
            componentName: 'JobDetailsBlockComponent'
        },
    };

    private pageComponentMap = {
        [PageComponentTypes.TEXT]: {
            fetch: () => import('../basic-text/basic-text.component'),
            componentName: 'BasicTextComponent'
        },
        [PageComponentTypes.CTA]: {
            fetch: () => import('../cta/cta.component'),
            componentName: 'CtaComponent'
        },
        [PageComponentTypes.JOB_HEADER]: {
            fetch: () => import('../header/header.component'),
            componentName: 'HeaderComponent'
        },
        [PageComponentTypes.HEADER]: {
            fetch: () => import('../header/header.component'),
            componentName: 'HeaderComponent'
        },
        [PageComponentTypes.IMAGE_TEXT]: {
            fetch: () => import('../image-with-text/image-with-text.component'),
            componentName: 'ImageWithTextComponent'
        },
        [PageComponentTypes.IMAGE]: {
            fetch: () => import('../images/images.component'),
            componentName: 'ImagesComponent'
        },
        [PageComponentTypes.QUOTE]: {
            fetch: () => import('../quote/quote.component'),
            componentName: 'QuoteComponent'
        },
        [PageComponentTypes.VIDEO]: {
            fetch: () => import('../video/video.component'),
            componentName: 'VideoComponent'
        },
        [PageComponentTypes.JOB_BOARD]: {
            fetch: () => import('../job-board/job-board.component'),
            componentName: 'JobBoardComponent'
        },
        [PageComponentTypes.GRID]: {
            fetch: () => import('../grid/grid.component'),
            componentName: 'GridComponent'
        },
        [PageComponentTypes.JOBS]: {
            fetch: () => import('../jobs/jobs.component'),
            componentName: 'JobsComponent'
        },
        [PageComponentTypes.JOB_BODY]: {
            fetch: () => import('../job-body/job-body.component'),
            componentName: 'JobBodyComponent'
        },
        [PageComponentTypes.JOB_SIDEBAR]: {
            fetch: () => import('../job-sidebar/job-sidebar.component'),
            componentName: 'JobSidebarComponent'
        },
        [PageComponentTypes.ACCORDION]: {
            fetch: () => import('../accordion/accordion.component'),
            componentName: 'AccordionComponent'
        },
        [PageComponentTypes.CAROUSEL]: {
            fetch: () => import('../card-carousel/card-carousel.component'),
            componentName: 'CardCarouselComponent',
        },
        [PageComponentTypes.JD_MAIN_HEADER]: {
            fetch: () => import('../../custom-components/jd/jd-main-header/jd-main-header.component'),
            componentName: 'JdMainHeaderComponent'
        },
        [PageComponentTypes.JD_JOB_BODY]: {
            fetch: () => import('../../custom-components/jd/jd-job-body/jd-job-body.component'),
            componentName: 'JdJobBodyComponent'
        },
        [PageComponentTypes.JD_INNER_PAGE_HEADER]: {
            fetch: () => import('../../custom-components/jd/jd-inner-page-header/jd-inner-page-header.component'),
            componentName: 'JdInnerPageHeaderComponent'
        },
        [PageComponentTypes.JD_SECTION_HEADER]: {
            fetch: () => import('../../custom-components/jd/jd-section-header/jd-section-header.component'),
            componentName: 'JdSectionHeaderComponent'
        },
        [PageComponentTypes.JD_LINK_GROUP]: {
            fetch: () => import('../../custom-components/jd/jd-link-group/jd-link-group.component'),
            componentName: 'JdLinkGroupComponent'
        },
        [PageComponentTypes.JD_JOB_SEARCH_HEADER]: {
            fetch: () => import('../../custom-components/jd/jd-job-search-header/jd-job-search-header.component'),
            componentName: 'JdJobSearchHeaderComponent'
        },
        [PageComponentTypes.JD_JOB_BOARD]: {
            fetch: () => import('../../custom-components/jd/jd-job-board/jd-job-board.component'),
            componentName: 'JdJobBoardComponent'
        },
        [PageComponentTypes.JD_LINKEDIN_FEED]: {
            fetch: () => import('../../custom-components/jd/jd-linkedin-feed/jd-linkedin-feed.component'),
            componentName: 'JdLinkedinFeedComponent'
        },
        [PageComponentTypes.JD_TIMELINE]: {
            fetch: () => import('../../custom-components/jd/jd-timeline/jd-timeline.component'),
            componentName: 'JdTimelineComponent'
        },
        [PageComponentTypes.JD_ANIMATED_STATS]: {
            fetch: () => import('../../custom-components/jd/jd-animated-stats/jd-animated-stats.component'),
            componentName: 'JdAnimatedStatsComponent'
        },
        [PageComponentTypes.JD_OUR_VALUES]: {
            fetch: () => import('../../custom-components/jd/jd-our-values/jd-our-values.component'),
            componentName: 'JdOurValuesComponent'
        },
        [PageComponentTypes.JD_OUR_COMMITMENTS]: {
            fetch: () => import('../../custom-components/jd/jd-our-commitments/jd-our-commitments.component'),
            componentName: 'JdOurCommitmentsComponent'
        },
        [PageComponentTypes.JD_FRANCE_HEADER]: {
            fetch: () => import('../../custom-components/jd/jd-france-header/jd-france-header.component'),
            componentName: 'JdFranceHeaderComponent'
        },
        [PageComponentTypes.JD_FRANCE_DEPARTMENTS_TABS]: {
            fetch: () => import('../../custom-components/jd/jd-france-department-tabs/jd-france-department-tabs.component'),
            componentName: 'JdFranceDepartmentTabsComponent'
        },
        [PageComponentTypes.JD_FRANCE_TRAINING_COURSES_TABS]: {
            fetch: () => import('../../custom-components/jd/jd-france-training-courses-tabs/jd-france-training-courses-tabs.component'),
            componentName: 'JdFranceTrainingCoursesTabsComponent'
        },
        [PageComponentTypes.JD_FRANCE_STORE_JOBS]: {
            fetch: () => import('../../custom-components/jd/jd-france-store-jobs/jd-france-store-jobs.component'),
            componentName: 'JdFranceStoreJobsComponent'
        },
        [PageComponentTypes.JD_FRANCE_SUCCESS_STORY]: {
            fetch: () => import('../../custom-components/jd/jd-france-success-story/jd-france-success-story.component'),
            componentName: 'JdFranceSuccessStoryComponent'
        },
        [PageComponentTypes.BREADCRUMBS]: {
            fetch: () => import('../breadcrumbs/breadcrumbs.component'),
            componentName: 'BreadcrumbsComponent'
        },
        [PageComponentTypes.SOHO_SEARCH_BOX]: {
            fetch: () => import('../../custom-components/soho-house/soho-search-box/soho-search-box.component'),
            componentName: 'SohoSearchBoxComponent'
        },
        [PageComponentTypes.TYPOGRAPHICAL_JOB_HEADER]: {
            fetch: () => import('../typographical-header/typographical-header.component'),
            componentName: 'TypographicalHeaderComponent'
        },
        [PageComponentTypes.TYPOGRAPHICAL_IMAGE_JOB_HEADER]: {
            fetch: () => import('../typographical-image-header/typographical-image-header.component'),
            componentName: 'TypographicalImageHeaderComponent'
        },
        [PageComponentTypes.SIDECAR_JOB_BODY]: {
            fetch: () => import('../sidecar-job-body/sidecar-job-body.component'),
            componentName: 'SidecarJobBodyComponent'
        },
        [PageComponentTypes.FULLSCREEN_JOB_HEADER]: {
            fetch: () => import('../fullscreen-job-header/fullscreen-job-header.component'),
            componentName: 'FullscreenJobHeaderComponent'
        },
        [PageComponentTypes.SIMPLE_TWO_COL_BODY]: {
            fetch: () => import('../simple-two-col-body/simple-two-col-body.component'),
            componentName: 'SimpleTwoColBodyComponent'
        },
        [PageComponentTypes.NEON_JOB_BODY]: {
            fetch: () => import('../neon-job-body/neon-job-body.component'),
            componentName: 'NeonJobBodyComponent'
        },
        [PageComponentTypes.TYPOGRAPHICAL_SEARCH_HEADER]: {
            fetch: () => import('../typographical-search-header/typographical-search-header.component'),
            componentName: 'TypographicalSearchHeaderComponent'
        },
        [PageComponentTypes.TYPOGRAPHICAL_JOB_BOARD]: {
            fetch: () => import('../typographical-job-board/typographical-job-board.component'),
            componentName: 'TypographicalJobBoardComponent'
        },
        [PageComponentTypes.MODERN_JOB_BOARD]: {
            fetch: () => import('../modern-job-board/modern-job-board.component'),
            componentName: 'ModernJobBoardComponent'
        },
        [PageComponentTypes.NEON_JOB_BOARD]: {
            fetch: () => import('../neon-job-board/neon-job-board.component'),
            componentName: 'NeonJobBoardComponent'
        },
        [PageComponentTypes.ALL_IN_ONE_SEARCH_HEADER]: {
            fetch: () => import('../all-in-one-search-header/all-in-one-search-header.component'),
            componentName: 'AllInOneSearchHeaderComponent'
        },
        [PageComponentTypes.JOB_CARDS]: {
            fetch: () => import('../job-cards/job-cards.component'),
            componentName: 'JobCardsComponent'
        },
        [PageComponentTypes.JD_FRANCE_INCLUSION_TABS]: {
            fetch: () => import('../../custom-components/jd/jd-france-inclusion-tabs/jd-france-inclusion-tabs.component'),
            componentName: 'JdFranceInclusionTabsComponent',
        },
        [PageComponentTypes.JD_PULLQUOTE]: {
            fetch: () => import('../../custom-components/jd/jd-pullquote/jd-pullquote.component'),
            componentName: 'JdPullquoteComponent',
        },
        [PageComponentTypes.MEET_THE_TEAM]: {
            fetch: () => import('../meet-the-team/meet-the-team.component'),
            componentName: 'MeetTheTeamComponent',
        },
        [PageComponentTypes.BASIC_JOB_BODY]: {
            fetch: () => import('../basic-job-body/basic-job-body.component'),
            componentName: 'BasicJobBodyComponent',
        },
        [PageComponentTypes.BASIC_JOB_HEADER]: {
            fetch: () => import('../basic-job-header/basic-job-header.component'),
            componentName: 'BasicJobHeaderComponent',
        },
    };

    private huskComponent = {
        fetch: () => import('../../grid-components/husk/husk.component'),
        componentName: 'HuskComponent'
    }

    constructor(
        public viewContainerRef: ViewContainerRef,
        private componentFactoryResolver: ComponentFactoryResolver,
    ) {}

    ngOnInit() {
        this.published = this.entity?.published;
        this.loadComponent();
    }

    ngOnChanges(changes: SimpleChanges) {
        // Always reload component if entity changes (navigation scenario)
        if (changes.entity) {
            this.loadComponent();
            return;
        }

        // Only reload component if component type has actually changed
        const componentTypeChanged = changes.component &&
            this.currentComponentType !== this.component.type;

        if (componentTypeChanged) {
            this.loadComponent();
        } else if (this.currentComponentRef) {
            // Just update the existing component's inputs for other changes
            this.updateComponentInputs();
        }
    }

    private async loadComponent() {
        const componentMap = (this.type === 'grid') ? this.gridComponentMap : this.pageComponentMap;
        const componentToFetch = componentMap[this.component.type];

        let result;
        if (!componentToFetch) {
            result = await this.huskComponent.fetch();
        } else {
            result = await componentToFetch.fetch();
        }

        this.createComponent(result[componentToFetch.componentName], componentToFetch.componentName);
    }

    private createComponent(componentClass: any, componentName: string) {
        // Always clear existing components to ensure proper lifecycle
        try {
            this.viewContainerRef.clear();
        } catch (error) {
            // Gracefully handle Cloudinary ngOnDestroy errors
            console.warn('Component cleanup error (likely Cloudinary):', error);
        }
        this.currentComponentRef = null;

        // Create component factory
        const componentFactory = this.componentFactoryResolver.resolveComponentFactory<AbstractComponent>(componentClass);

        // Create new component
        const componentRef = this.viewContainerRef.createComponent<AbstractComponent>(componentFactory);
        this.currentComponentRef = componentRef;
        this.currentComponentType = this.component.type;

        // Set up component properties
        this.updateComponentInputs();
    }

    private updateComponentInputs() {
        if (!this.currentComponentRef) return;

        const instance = this.currentComponentRef.instance;
        instance.componentIndex = this.componentIndex;
        instance.rowIndex = this.rowIndex;
        instance.gridIndex = this.gridIndex;
        instance.component = this.component;
        instance.noTracking = this.noTracking;
        instance.editMode = this.editMode;
        instance.templateLocked = this.templateLocked;
        instance.mobileMode = this.mobileMode;
        instance.published = this.published;
        instance.fullWidth = this.row?.fullWidth || false;

        // Add entity-specific properties
        if (this.entity?.hasOwnProperty('active')) {
            instance['job'] = this.entity as Job;
        } else {
            instance['page'] = this.entity as Page;
        }

        if (this.entity?.hasOwnProperty('lang')) {
            instance['lang'] = this.entity.lang;
        }

        // Add CSS classes
        const element = this.currentComponentRef.location.nativeElement;
        element.classList.add('w-full', 'h-full');
    }

    ngOnDestroy() {
        // Ensure proper cleanup when component resolver is destroyed
        if (this.currentComponentRef) {
            try {
                this.viewContainerRef.clear();
            } catch (error) {
                console.warn('Component cleanup error during destroy:', error);
            }
            this.currentComponentRef = null;
            this.currentComponentType = null;
        }
    }
}
